<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update']);

const store = useStore();

// Rótulos como constantes (evita bare-strings no template) — texto próprio,
// já que não há chaves i18n para telefones múltiplos ainda.
const LABELS = {
  title: 'Telefones',
  primary: 'Principal',
  empty: 'Nenhum telefone salvo',
  add: 'Adicionar',
  placeholder: 'Novo telefone (ex.: +55 48 90000-0000)',
  error: 'Não foi possível salvar o telefone.',
  whatsapp: 'Abrir no WhatsApp',
  remove: 'Remover telefone',
};

// Telefones adicionais salvos em additional_attributes.phones (array de
// strings). Estado local otimista para refletir na hora após salvar.
const localPhones = ref(null);

const additionalPhones = computed(() => {
  if (localPhones.value != null) return localPhones.value;
  const arr = props.contact?.additional_attributes?.phones;
  return Array.isArray(arr) ? arr : [];
});

// Lista completa: principal (contact.phone_number) + adicionais, sem repetir.
const allPhones = computed(() => {
  const primary = props.contact?.phone_number;
  const list = [];
  if (primary) list.push({ number: primary, primary: true });
  additionalPhones.value.forEach(number => {
    if (number && number !== primary) list.push({ number, primary: false });
  });
  return list;
});

const newPhone = ref('');
const isSaving = ref(false);

const persist = async phones => {
  if (!props.contact?.id) return;
  isSaving.value = true;
  localPhones.value = phones; // otimista
  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      additional_attributes: {
        ...(props.contact.additional_attributes || {}),
        phones,
      },
    });
    emit('update');
  } catch (error) {
    useAlert(LABELS.error);
  } finally {
    isSaving.value = false;
  }
};

const addPhone = async () => {
  const number = newPhone.value.trim();
  if (!number || isSaving.value) return;
  if (allPhones.value.some(p => p.number === number)) {
    newPhone.value = '';
    return;
  }
  await persist([...additionalPhones.value, number]);
  newPhone.value = '';
};

const removePhone = async number => {
  await persist(additionalPhones.value.filter(n => n !== number));
};

const waLink = number => `https://wa.me/${String(number).replace(/\D/g, '')}`;
</script>

<template>
  <CardLayout>
    <h3
      class="flex items-center gap-1.5 text-[13px] font-semibold text-n-slate-12"
    >
      <span class="i-lucide-phone text-sm text-n-slate-10" />
      {{ LABELS.title }}
    </h3>

    <div class="flex flex-col w-full gap-1.5">
      <div
        v-for="p in allPhones"
        :key="p.number"
        class="flex items-center gap-2 py-1 group"
      >
        <span class="shrink-0 i-lucide-phone size-3.5 text-n-slate-10" />
        <span
          class="flex-1 min-w-0 text-xs truncate text-n-slate-12 tabular-nums"
        >
          {{ p.number }}
        </span>
        <span
          v-if="p.primary"
          class="shrink-0 rounded-full bg-n-teal-3 px-1.5 py-0.5 text-[10px] font-medium text-n-teal-11"
        >
          {{ LABELS.primary }}
        </span>
        <a
          v-tooltip.top="LABELS.whatsapp"
          :href="waLink(p.number)"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center justify-center rounded-md shrink-0 size-6 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-teal-11"
        >
          <span class="i-lucide-message-circle size-3.5" />
        </a>
        <button
          v-if="!p.primary"
          v-tooltip.top="LABELS.remove"
          type="button"
          class="flex items-center justify-center rounded-md shrink-0 size-6 text-n-slate-11 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-n-ruby-3 hover:text-n-ruby-11"
          @click="removePhone(p.number)"
        >
          <span class="i-lucide-trash-2 size-3.5" />
        </button>
      </div>

      <p v-if="!allPhones.length" class="text-xs text-n-slate-10">
        {{ LABELS.empty }}
      </p>

      <div class="flex items-center gap-1.5 pt-1">
        <Input
          v-model="newPhone"
          size="sm"
          :placeholder="LABELS.placeholder"
          class="flex-1 min-w-0"
          @enter="addPhone"
        />
        <Button
          size="xs"
          icon="i-lucide-plus"
          :label="LABELS.add"
          :is-loading="isSaving"
          :disabled="!newPhone.trim()"
          @click="addPhone"
        />
      </div>
    </div>
  </CardLayout>
</template>
